import { createFirebaseAdapter } from '../firebase/firebaseAdapter';
import { createFirebaseClient } from '../firebase/firebaseClient';
import { getUserPropertiesFromDimensions } from '../google-analytics/ga4';
import type { CustomDimensionsOrParams } from '../google-analytics/ga4Types';
import { trackUserProperties } from '../google-analytics/userProperties';
import { gtagWrapper } from '../google-analytics/utils';
import { trackPage } from '../thirdPartyTrackingAgent/pages';
import {
  handleECommerceTransactionDataError,
  handleGa4EventNameError,
} from './error';
import type { ECommerce4Transaction, TrackECommerceArg } from './types';
import {
  getEcommerceData,
  getVirtualPageName,
  isGa4EcommerceData,
} from './utils';

const trackECommerceGa4 = (
  eCommerceData: ECommerce4Transaction,
  eventName = 'purchase'
): void => {
  gtagWrapper('event', eventName, eCommerceData);
};

const trackWebECommerce = (
  ga4Data: ECommerce4Transaction,
  eventName: string
) => {
  trackECommerceGa4(ga4Data, eventName);
};

const trackNativeECommerce = (
  ga4Data: ECommerce4Transaction,
  eventName: string
) => {
  const firebaseAdapter = createFirebaseAdapter();
  const firebaseClient = createFirebaseClient(firebaseAdapter);
  firebaseClient.trackECommerce(ga4Data, eventName);
};

export const trackECommerceData = (
  eCommerceData: TrackECommerceArg | ECommerce4Transaction
) => {
  const { ga4Data } = getEcommerceData(eCommerceData);
  handleECommerceTransactionDataError(ga4Data);
  const eventName = ga4Data?.event_name || 'purchase';
  handleGa4EventNameError(eventName);
  trackWebECommerce(ga4Data, eventName);
  trackNativeECommerce(ga4Data, eventName);
};

export const trackConfirmation = async (
  eventName: string,
  screenName?: string
) => {
  await trackPage(
    screenName || getVirtualPageName(eventName) || 'confirmation',
    {
      funnel: true,
    }
  );
};

export const trackECommerceUserProperties = (
  eCommerceData: TrackECommerceArg | ECommerce4Transaction
) => {
  const products = isGa4EcommerceData(eCommerceData)
    ? eCommerceData.items
    : eCommerceData.products;
  products?.forEach((item) => {
    const eCommerceUserProperties = getUserPropertiesFromDimensions(
      item as CustomDimensionsOrParams
    );
    trackUserProperties(eCommerceUserProperties);
  });
};
